import {
  Button,
  EmptyState,
  FilterSegments,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@westhetics/ui'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import {
  OPERATION_AREA_FILTERS,
  useOperationFailures,
  type OperationAreaFilter,
  type OperationsPeriod,
} from './api'
import { AREA_LABELS, PERIOD_PHRASES, ago, noFailureTitle, occurrencesText } from './labels'

/**
 * Falhas (RN-ADM-98): as quatro origens na mesma lista, as mais recentes primeiro, com o número de cada área
 * no segmento. A área e a página ficam no endereço. Nenhum dado de paciente: clínica, causa e quando.
 */
export function FailuresSection({
  period,
  area,
  page,
  now,
  onAreaChange,
  onPageChange,
  onSeeWeek,
}: {
  period: OperationsPeriod
  area: OperationAreaFilter
  page: number
  now: number
  onAreaChange: (area: OperationAreaFilter) => void
  onPageChange: (page: number) => void
  onSeeWeek: () => void
}) {
  const query = useOperationFailures({ period, area, page })
  const counts = query.data?.meta.counts
  const items = query.data?.data ?? []

  return (
    <section id="falhas" aria-labelledby="operations-failures" className="flex flex-col gap-4">
      <h2 id="operations-failures" className="eyebrow">
        Falhas
      </h2>
      <FilterSegments
        label="Filtrar falhas por área"
        value={area}
        onChange={onAreaChange}
        options={OPERATION_AREA_FILTERS.map((value) => ({
          value,
          label: AREA_LABELS[value],
          count: counts?.[value],
        }))}
      />

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar as falhas"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <TableSkeleton rows={4} columns={4} />
      ) : items.length === 0 ? (
        <EmptyState
          title={noFailureTitle(area, period)}
          description="Quando algo falhar, aparece aqui com a causa e a clínica."
          action={
            area !== 'all' ? (
              <Button
                className="max-desk:min-h-touch"
                variant="outline"
                onClick={() => onAreaChange('all')}
              >
                Ver todas as áreas
              </Button>
            ) : period === 'day' ? (
              <Button className="max-desk:min-h-touch" variant="outline" onClick={onSeeWeek}>
                Ver os últimos 7 dias
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <tr>
              <TableHead>Quando</TableHead>
              <TableHead>Área</TableHead>
              <TableHead>O que aconteceu</TableHead>
              <TableHead>Clínica</TableHead>
            </tr>
          </TableHeader>
          <TableBody>
            {items.map((failure) => {
              const repeated = occurrencesText(failure.occurrences, failure.firstOccurredAt)
              return (
                <TableRow key={failure.id}>
                  <TableCell label="Quando" muted>
                    <span className="flex flex-col">
                      <span>{ago(failure.occurredAt, now)}</span>
                      {repeated ? <span className="text-label">{repeated}</span> : null}
                    </span>
                  </TableCell>
                  <TableCell label="Área" muted>
                    {AREA_LABELS[failure.area]}
                  </TableCell>
                  <TableCell primary>
                    <span className="flex flex-col gap-1">
                      <span>{failure.title}</span>
                      <span className="font-body text-secondary text-[12px] leading-[1.5] font-light">
                        {failure.cause}
                      </span>
                      {failure.detail && failure.detail !== failure.cause ? (
                        <span className="font-body text-label text-[11px] leading-[1.5] font-light break-all">
                          {failure.detail}
                        </span>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell label="Clínica" muted={!failure.clinic}>
                    {failure.clinic?.name ?? '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
      {query.data && items.length > 0 ? (
        <ListFooter
          meta={query.data.meta}
          noun={[`falha ${PERIOD_PHRASES[period]}`, `falhas ${PERIOD_PHRASES[period]}`]}
          note="as mais recentes primeiro"
          onPage={onPageChange}
        />
      ) : null}
    </section>
  )
}
